import { EditOutlined } from '@ant-design/icons';
import { Card, Row, Tag } from 'antd';
import * as Moment from 'moment';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { EventType, ScheduledEventItem, ScheduledEventSummary, ScheduledEventSummaryStore } from 'src/stores/scheduled-events-store';
import { ScheduledEventDetails } from './scheduled-event-details';

const ScheduledEventsListPage: FC = () => {
  const { t, i18n } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'createdOn', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const eventsStore = container.get(ScheduledEventSummaryStore);
  const scheduledEventsState = eventsStore.state;
  const [scheduledEventDetails, setScheduledEventDetails] = useState<ScheduledEventSummary | undefined>();
  const [newScheduledEventVisible, setNewScheduledEventVisible] = useState<boolean>(false);

  const selectedRows = useRef<Array<string>>([]);
  const itemsJustDeleted = useRef(false);

  setHeader(t('ScheduledEvents'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'scheduledEvents', breadcrumbName: t('ScheduledEvents') },
  ]);

  useEffect(
    () => {
      eventsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'createdOn',
        field: 'createdOn',
        title: t('CreatedOn'),
        renderer: (data) => <span>{Moment.utc(data.createdOn).local().locale(i18n.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        sortable: true,
        filter: <OptionsFilter options={Object.entries(EventType).map(([key, value]) => ({ label: t(key), value: value }))} />,
        fiterField: 'type',
        field: 'type',
        title: t('EventType'),
        renderer: (data) => <Tag>{t(`${Object.entries(EventType).find((v) => v[1] === data.type)?.[0]}`)}</Tag>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'AdStartDate',
        field: 'AdStartDate',
        title: t('VisibilityStartDate'),
        renderer: (data) => <span>{Moment.utc(data.announcementStartDate).local().locale(i18n.language).format(`${getDateFormat()}`)}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'AdEndDate',
        field: 'AdEndDate',
        title: t('VisibilityEndDate'),
        renderer: (data) => <span>{Moment.utc(data.announcementEndDate).local().locale(i18n.language).format(`${getDateFormat()}`)}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'scheduledUpdateStart',
        field: 'scheduledUpdateStart',
        title: t('EventStart'),
        renderer: (data) => (
          <span>
            {data.type === EventType.ScheduledUpdate
              ? Moment.utc(data.scheduledUpdateStart).local().locale(i18n.language).format(`${getDateFormat()}, hh:mm a`)
              : '-'}
          </span>
        ),
      },
      {
        sortable: true,
        fiterField: 'announcementDuration',
        field: 'announcementDuration',
        title: t('EventDuration'),
        renderer: (data) => <span> {data.type === EventType.ScheduledUpdate ? data.announcementDuration : '-'}</span>,
      },
      {
        fixed: 'right',
        sortable: false,
        searchable: false,
        field: 'edit',
        title: t('Edit'),
        renderer: (data) => {
          let itemData = data as ScheduledEventItem;
          return (
            <span
              className="clickable-span strong"
              onClick={() => {
                setScheduledEventDetails(() => itemData);
              }}
            >
              {<EditOutlined />}
            </span>
          );
        },
      },
    ],
    data: scheduledEventsState.value,
  } as TableModel<ScheduledEventSummary>;

  return (
    <>
      <Row align="top" justify="space-between" className="table-overflow">
        <Card className="card-table" style={{ width: '100%' }}>
          <TableView
            canCreateNew={true}
            onNewItem={() => {
              setNewScheduledEventVisible(true);
            }}
            onSelectionChanged={(rows: Array<string>) => {
              selectedRows.current = rows;
            }}
            newItemName={t('Add')}
            canDelete={true}
            onDeleteRow={async (item: any) => {
              if (!itemsJustDeleted.current) {
                itemsJustDeleted.current = true;
                await eventsStore.deleteItems(selectedRows.current);
                itemsJustDeleted.current = false;
              }
            }}
            canSort
            onQueryChanged={(query: Query) => {
              setQuery(query);
              eventsStore.load(query);
            }}
            onRefresh={() => eventsStore.load(query)}
            model={scheduledEventsState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              scheduledEventsState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(scheduledEventsState.errorMessage.value) : t('ScheduledEventsTableError'))
            }
          />
        </Card>
      </Row>

      {scheduledEventDetails !== undefined && (
        <ScheduledEventDetails
          mode="edit"
          item={scheduledEventDetails}
          onClose={(response) => {
            setScheduledEventDetails(undefined);
            if (response?.isSuccess) eventsStore.load(query);
          }}
        />
      )}
      {newScheduledEventVisible && (
        <ScheduledEventDetails
          mode="new"
          onClose={(response) => {
            setNewScheduledEventVisible(false);
            if (response?.isSuccess) eventsStore.load(query);
          }}
        />
      )}
    </>
  );
};

export default ScheduledEventsListPage;
